// src/api/journalApi.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL || 
  'https://us-central1-beauty-salon-b2e61.cloudfunctions.net';

/**
 * Client-side API service for journal operations
 * Calls Firebase Functions for encrypted journal entries
 */
class JournalAPI {
  /**
   * Retry mechanism for API requests
   * @private
   */
  static async _retryRequest(requestFn, maxRetries = 2) {
    let lastError;
    let delay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        if (attempt < maxRetries && this._isRetriableError(error)) {
          console.log(`API: Retrying request in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2;
        }
      }
    }
    throw lastError;
  }

  /**
   * Check if error is retriable
   * @private
   */
  static _isRetriableError(error) {
    return error.message?.includes('network') || 
           error.message?.includes('timeout') ||
           error.message?.includes('fetch');
  }

  /**
   * Handle API errors
   * @private
   */
  static async _handleAPIError(response, operation) {
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (e) {
      // If JSON parsing fails, use default error message
    }
    
    console.error(`API Error in ${operation}:`, errorMessage);
    throw new Error(errorMessage);
  }

  /**
   * Get paginated journal entries
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of entries per page
   * @param {string|null} customerId - Optional customer filter
   * @returns {Promise<Object>} Paginated entries response
   */
  static async getEntriesPaginated(page = 1, pageSize = 20, customerId = null, sortOrder = 'desc') {
    try {
      console.log('API: Getting paginated journal entries', { page, pageSize, customerId, sortOrder });

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`);
        url.searchParams.set('operation', 'getEntriesPaginated');
        url.searchParams.set('page', page.toString());
        url.searchParams.set('pageSize', pageSize.toString());
        url.searchParams.set('sortOrder', sortOrder);
        if (customerId) {
          url.searchParams.set('customerId', customerId);
        }

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getEntriesPaginated');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved entries', {
          page,
          count: result.data?.length || 0,
          total: result.total,
          hasMore: result.hasMore
        });

        return result;
      });

    } catch (error) {
      console.error('API Error in getEntriesPaginated:', {
        page,
        pageSize,
        customerId,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to fetch journal entries: ${error.message}`);
    }
  }

  /**
   * Create new journal entry
   * @param {Object} entryData - Entry data with encrypted content
   * @returns {Promise<Object>} Created entry
   */
  static async createEntry(entryData) {
    try {
      console.log('API: Creating journal entry', { 
        customerId: entryData.customer_id,
        sessionNumber: entryData.session_number 
      });

      if (!entryData) {
        throw new Error('entryData is required');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`;

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'createEntry',
            ...entryData
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'createEntry');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully created entry', {
          entryId: result.data?.id
        });

        return result;
      });

    } catch (error) {
      console.error('API Error in createEntry:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to create journal entry: ${error.message}`);
    }
  }

  /**
   * Update existing journal entry
   * @param {string} entryId - Entry ID
   * @param {Object} entryData - Updated entry data
   * @returns {Promise<Object>} Updated entry
   */
  static async updateEntry(entryId, entryData) {
    try {
      console.log('API: Updating journal entry', { entryId });

      if (!entryId) {
        throw new Error('entryId is required');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`;

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'updateEntry',
            entryId,
            ...entryData
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'updateEntry');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully updated entry', {
          entryId: result.data?.id
        });

        return result;
      });

    } catch (error) {
      console.error('API Error in updateEntry:', {
        entryId,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to update journal entry: ${error.message}`);
    }
  }

  /**
   * Delete journal entry
   * @param {string} entryId - Entry ID
   * @returns {Promise<Object>} Success response
   */
  static async deleteEntry(entryId) {
    try {
      console.log('API: Deleting journal entry', { entryId });

      if (!entryId) {
        throw new Error('entryId is required');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`;

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'deleteEntry',
            entryId
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'deleteEntry');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully deleted entry', { entryId });

        return result;
      });

    } catch (error) {
      console.error('API Error in deleteEntry:', {
        entryId,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to delete journal entry: ${error.message}`);
    }
  }

  /**
   * Setup journal password in Firestore
   * @param {string} passwordHash - Hashed password
   * @returns {Promise<Object>} Success response
   */
  static async setupPassword(passwordHash) {
    try {
      console.log('API: Setting up journal password');

      if (!passwordHash) {
        throw new Error('passwordHash is required');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`;

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'setupPassword',
            passwordHash
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'setupPassword');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully setup password');

        return result;
      });

    } catch (error) {
      console.error('API Error in setupPassword:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to setup journal password: ${error.message}`);
    }
  }

  /**
   * Verify journal password
   * @param {string} passwordHash - Hashed password to verify
   * @returns {Promise<Object>} Verification result with salt if valid
   */
  static async verifyPassword(passwordHash) {
    try {
      console.log('API: Verifying journal password');

      if (!passwordHash) {
        throw new Error('passwordHash is required');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`;

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'verifyPassword',
            passwordHash
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'verifyPassword');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Password verification result:', {
          isValid: result.data?.isValid
        });

        return result;
      });

    } catch (error) {
      console.error('API Error in verifyPassword:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to verify journal password: ${error.message}`);
    }
  }

  /**
   * Get journal password status
   * @returns {Promise<Object>} Password status (hasPassword, salt)
   */
  static async getPasswordStatus() {
    try {
      console.log('API: Getting password status');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/journalHandler`);
        url.searchParams.set('operation', 'getPasswordStatus');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getPasswordStatus');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved password status', {
          hasPassword: result.data?.hasPassword
        });

        return result;
      });

    } catch (error) {
      console.error('API Error in getPasswordStatus:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get password status: ${error.message}`);
    }
  }

  /**
   * Test API connectivity
   * @returns {Promise<boolean>} True if API is accessible
   */
  static async testConnection() {
    try {
      console.log('API: Testing journal connection...');
      await this.getPasswordStatus();
      console.log('API: Journal connection test successful');
      return true;
    } catch (error) {
      console.error('API: Journal connection test failed:', error.message);
      return false;
    }
  }
}

export { JournalAPI };